import { useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { toast } from 'sonner';
import {
  IndianRupee, RefreshCw, Paperclip, Upload, Trash2, Eye, FileImage, Users,
} from 'lucide-react';
import type { ApiError } from '@pms/shared';
import { PERMISSIONS } from '@pms/shared';
import {
  financeService, facultyFileUrl, type FacultyPayment, type FacultyPaymentFile,
} from '@/services/finance.service';
import { StatCard } from '@/components/ui/stat-card';
import { DataTable, type Column } from '@/components/ui/data-table';
import { SearchInput } from '@/components/ui/search-input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { usePermission } from '@/hooks/usePermission';
import { formatINR } from '@/lib/currency';
import { formatDate } from '@/lib/dateFormat';

const ALL = '__all__';

const STATUS_VARIANT: Record<FacultyPayment['status'], 'success' | 'warning' | 'destructive'> = {
  paid: 'success',
  pending: 'warning',
  cancelled: 'destructive',
};

function errMsg(e: unknown): string {
  return (e as AxiosError<ApiError>).response?.data?.error?.message ?? 'Something went wrong';
}

function formatSize(bytes: number): string {
  return bytes < 1024 * 1024 ? `${(bytes / 1024).toFixed(0)} KB` : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ── Status dropdown — inline mutation, mirrors AssetStatusDropdown pattern ────

function FacultyPaymentStatusCell({ payment }: { payment: FacultyPayment }) {
  const queryClient = useQueryClient();
  const perms = usePermission();

  const mutation = useMutation({
    mutationFn: (status: FacultyPayment['status']) =>
      financeService.updateFacultyPaymentStatus(payment.id, status),
    onSuccess: () => {
      toast.success('Status updated');
      void queryClient.invalidateQueries({ queryKey: ['finance', 'faculty-payments'] });
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  if (!perms.has(PERMISSIONS.FINANCE_FACULTY_PAYMENT_MANAGE)) {
    return <Badge variant={STATUS_VARIANT[payment.status]}>{payment.status}</Badge>;
  }

  return (
    <Select
      value={payment.status}
      onValueChange={(v) => mutation.mutate(v as FacultyPayment['status'])}
      disabled={mutation.isPending}
    >
      <SelectTrigger className="h-8 w-32 text-xs capitalize">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="pending">Pending</SelectItem>
        <SelectItem value="paid">Paid</SelectItem>
        <SelectItem value="cancelled">Cancelled</SelectItem>
      </SelectContent>
    </Select>
  );
}

// ── File dialog ───────────────────────────────────────────────────────────────

function FacultyPaymentFilesDialog({
  payment,
  onClose,
}: {
  payment: FacultyPayment;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const perms = usePermission();
  const canManage = perms.has(PERMISSIONS.FINANCE_FACULTY_PAYMENT_MANAGE);
  const fileRef = useRef<HTMLInputElement>(null);

  const { data: files = [], isLoading } = useQuery({
    queryKey: ['finance', 'faculty-payments', payment.id, 'files'],
    queryFn: () => financeService.listFacultyPaymentFiles(payment.id),
  });

  const uploadMutation = useMutation({
    mutationFn: (file: File) => financeService.uploadFacultyPaymentFile(payment.id, file),
    onSuccess: () => {
      toast.success('File uploaded');
      void queryClient.invalidateQueries({ queryKey: ['finance', 'faculty-payments', payment.id, 'files'] });
    },
    onError: (e) => toast.error(errMsg(e)),
    onSettled: () => { if (fileRef.current) fileRef.current.value = ''; },
  });

  const deleteMutation = useMutation({
    mutationFn: (fileId: number) => financeService.deleteFacultyPaymentFile(fileId),
    onSuccess: () => {
      toast.success('File deleted');
      void queryClient.invalidateQueries({ queryKey: ['finance', 'faculty-payments', payment.id, 'files'] });
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Paperclip className="h-4 w-4" />
            {payment.faculty_name} — {formatINR(payment.amount)}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {canManage && (
            <div
              onClick={() => fileRef.current?.click()}
              className="cursor-pointer rounded-xl border-2 border-dashed border-border p-5 text-center transition-colors hover:border-primary/50 hover:bg-primary/5"
            >
              <input
                ref={fileRef}
                type="file"
                className="hidden"
                accept="image/*,.pdf"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadMutation.mutate(f); }}
              />
              {uploadMutation.isPending ? (
                <div className="flex flex-col items-center gap-2">
                  <RefreshCw className="h-6 w-6 animate-spin text-primary" />
                  <p className="text-sm text-muted-foreground">Uploading…</p>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                    <Upload className="h-5 w-5 text-primary" />
                  </div>
                  <p className="text-sm font-medium">Upload screenshot or PDF</p>
                  <p className="text-xs text-muted-foreground">Proof of payment</p>
                </div>
              )}
            </div>
          )}

          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Uploaded Files ({files.length})
            </p>
            {isLoading ? (
              <div className="flex justify-center py-4"><RefreshCw className="h-5 w-5 animate-spin text-muted-foreground" /></div>
            ) : files.length === 0 ? (
              <p className="py-4 text-center text-sm text-muted-foreground">No files yet</p>
            ) : (
              <div className="max-h-52 space-y-2 overflow-y-auto">
                {files.map((f: FacultyPaymentFile) => (
                  <div key={f.id} className="flex items-center gap-3 rounded-lg border bg-muted/30 p-2.5">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                      <FileImage className="h-4 w-4 text-primary" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-xs font-medium">{f.original_name}</p>
                      <p className="text-[10px] text-muted-foreground">{formatSize(f.file_size)}</p>
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      <button
                        onClick={() => window.open(facultyFileUrl(f.file_name), '_blank', 'noopener')}
                        className="rounded-lg p-1.5 text-blue-500 transition-colors hover:bg-blue-100"
                        title="View"
                      >
                        <Eye className="h-3.5 w-3.5" />
                      </button>
                      {canManage && (
                        <button
                          onClick={() => deleteMutation.mutate(f.id)}
                          disabled={deleteMutation.isPending}
                          className="rounded-lg p-1.5 text-rose-500 transition-colors hover:bg-rose-100 disabled:opacity-50"
                          title="Delete"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function FacultyPaymentsPage() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(ALL);
  const [filesTarget, setFilesTarget] = useState<FacultyPayment | null>(null);

  const { data: payments = [], isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ['finance', 'faculty-payments'],
    queryFn: () => financeService.listFacultyPayments(),
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return payments.filter((p) => {
      const matchesSearch = !q
        || p.faculty_name?.toLowerCase().includes(q)
        || p.faculty_code?.toLowerCase().includes(q);
      const matchesStatus = statusFilter === ALL || p.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [payments, search, statusFilter]);

  const totalPaid = useMemo(
    () => payments.filter((p) => p.status === 'paid').reduce((s, p) => s + Number(p.amount || 0), 0),
    [payments],
  );
  const totalPending = useMemo(
    () => payments.filter((p) => p.status === 'pending').reduce((s, p) => s + Number(p.amount || 0), 0),
    [payments],
  );

  const columns: Column<FacultyPayment>[] = [
    {
      key: 'faculty', header: 'Faculty',
      cell: (p) => (
        <div>
          <p className="text-sm font-medium">{p.faculty_name || '—'}</p>
          <p className="text-xs text-muted-foreground">{p.faculty_code || ''}</p>
        </div>
      ),
    },
    { key: 'amount', header: 'Amount', cell: (p) => <span className="font-semibold tabular-nums">{formatINR(p.amount)}</span> },
    { key: 'type', header: 'Type', cell: (p) => <span className="text-muted-foreground">{p.payment_type || '—'}</span> },
    { key: 'method', header: 'Method', cell: (p) => <span className="text-muted-foreground">{p.payment_method || '—'}</span> },
    { key: 'date', header: 'Date', cell: (p) => <span className="whitespace-nowrap text-sm text-muted-foreground">{p.payment_date ? formatDate(p.payment_date) : '—'}</span> },
    { key: 'status', header: 'Status', cell: (p) => <FacultyPaymentStatusCell payment={p} /> },
    {
      key: 'files', header: 'Files',
      cell: (p) => (
        <button
          onClick={() => setFilesTarget(p)}
          className="flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-primary/10 hover:text-primary"
        >
          <Paperclip className="h-3.5 w-3.5" /> Files
        </button>
      ),
    },
  ];

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold tracking-tight">Faculty Payments</h1>
          <p className="text-sm text-muted-foreground">Live data from the Faculty Management System</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => void refetch()} disabled={isFetching}>
          <RefreshCw className={`mr-1.5 h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <StatCard label="Total Paid" value={formatINR(totalPaid)} icon={IndianRupee} tone="emerald" />
        <StatCard label="Total Pending" value={formatINR(totalPending)} icon={IndianRupee} tone="amber" />
        <StatCard label="Records" value={payments.length} icon={Users} tone="sky" />
      </div>

      <div className="flex flex-col gap-3 sm:flex-row">
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search by faculty name or code…"
          containerClassName="w-full sm:max-w-xs"
        />
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-40"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <h2 className="text-lg font-semibold">Payment Records ({filtered.length})</h2>
        <DataTable
          columns={columns}
          rows={filtered}
          rowKey={(p) => String(p.id)}
          loading={isLoading}
          error={isError}
          onRetry={() => void refetch()}
          empty={search || statusFilter !== ALL ? 'No payments match your filters.' : 'No faculty payment records found.'}
        />
      </div>

      {filesTarget && (
        <FacultyPaymentFilesDialog payment={filesTarget} onClose={() => setFilesTarget(null)} />
      )}
    </div>
  );
}
